import axios from "axios";
import Holidays from "date-holidays";
import type { ManipulateType } from "dayjs";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import localizedFormat from "dayjs/plugin/localizedFormat";
import utc from "dayjs/plugin/utc";
import { cloneElement } from "react";
import type { IconType } from "react-icons";
import type { DateRangeType, DateType } from "react-tailwindcss-datepicker";
import { countyOptions } from "./countyOptions";
import { reportInvalidDate } from "./invalidDateReporter";
import type {
  countryType,
  countyOptionsType,
  optionType,
  parentIdOptionType,
  priceSearchResultType,
  Translationtype,
} from "./types";

// eslint-disable-next-line import/no-named-as-default-member
dayjs.extend(utc);
// eslint-disable-next-line import/no-named-as-default-member
dayjs.extend(isBetween);
// eslint-disable-next-line import/no-named-as-default-member
dayjs.extend(localizedFormat);

export function convertDate(
  date: string | Date,
  format?:
    | "date"
    | "datetime"
    | "year"
    | "utc"
    | "localeToUtc"
    | "utcEnd"
    | "utcToLocaleDate"
    | "utcToLocaleDatetime"
    | "utcL"
    | "simpleDateFormat"
    | "localeToUtcStart"
    | "localeToUtcEnd",
  i18n?: string
) {
  if (!date) {
    return "-";
  }

  // Unified parsing: handles Date objects, ISO strings, and YYYY-MM-DD strings.
  let parsed = dayjs(date);

  // Fallback for legacy string normalisation (strips trailing Z and re-appends .000Z)
  // so that strings like "YYYY-MM-DDTHH:MM:SS.mmmZ" still work as before.
  if (!parsed.isValid() && typeof date === "string") {
    const dateStr = date.replace(/Z$/, "");
    const isoStr = dateStr.includes("T")
      ? `${dateStr.split(".")[0]}.000Z`
      : `${dateStr}T00:00:00.000Z`;
    parsed = dayjs(new Date(isoStr));
  }

  if (!parsed.isValid()) {
    reportInvalidDate(date, format);
    return "-";
  }

  const _date = i18n ? parsed.locale(i18n) : parsed;

  switch (format) {
    case "date":
      return i18n
        ? _date.format("L")
        : _date.format("YYYY-MM-DD");
    case "datetime":
      return i18n
        ? _date.locale(i18n || "hu").format("L LT")
        : _date.format("YYYY-MM-DD HH:mm:ss");
    case "year":
      return _date.format("YYYY");
    case "utc": {
      const utcTime = dayjs.utc(date).local().format("HH:mm:ss");
      if (utcTime === "00:00:00" || utcTime === "23:59:59") {
        return dayjs.utc(date).local().format("YYYY-MM-DD");
      }
      return dayjs.utc(date).local().format("YYYY-MM-DD HH:mm:ss");
    }

    case "utcL": {
      const utcTime = dayjs.utc(date).local().format("HH:mm:ss");
      if (utcTime === "00:00:00" || utcTime === "23:59:59") {
        return dayjs
          .utc(date)
          .local()
          .locale(i18n || "hu")
          .format("L");
      }
      return dayjs
        .utc(date)
        .local()
        .locale(i18n || "hu")
        .format("L LT");
    }

    case "utcEnd": {
      return _date.endOf("day").format("YYYY-MM-DD HH:mm:ss");
    }
    case "utcToLocaleDate": {
      // convert UTC date to locale date. Format: YYYY-MM-DD
      return i18n
        ? dayjs.utc(date).local().format("L")
        : dayjs.utc(date).local().format("YYYY-MM-DD");
    }
    case "utcToLocaleDatetime": {
      // convert UTC date to locale datetime. Format: DD.MM.YYYY hh:mm
      return i18n
        ? dayjs
            .utc(date)
            .local()
            .locale(i18n || "hu")
            .format("L LT")
        : dayjs.utc(date).local().format("DD.MM.YYYY hh:mm");
    }
    case "localeToUtc": {
      return _date.utc().format("YYYY-MM-DD HH:mm:ss");
    }
    case "localeToUtcStart": {
      return _date.startOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
    }
    case "localeToUtcEnd": {
      return _date.endOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
    }
    case "simpleDateFormat": {
      return _date.locale(i18n || "hu").format("L");
    }
    default:
      return _date.format("YYYY-MM-DD HH:mm:ss");
  }
}

export function generateIcon(
  icon: IconType,
  className?: string,
  handleClick?: () => void
): JSX.Element {
  const CustomIcon = icon;
  return <CustomIcon className={className || ""} onClick={handleClick} />;
}

export function findDeviceStatus(
  statusCode: number | undefined,
  options: optionType[]
) {
  if (statusCode === undefined || null) {
    return "";
  }
  const value = options.find((item: optionType) => item.value === statusCode);
  return value && value.label;
}

export function findDeviceType(
  typeId: string,
  deviceTypeOptions: optionType[]
) {
  if (typeId) {
    const value = deviceTypeOptions.find((item) => item.value === typeId);
    return value && value.label;
  }
  return "";
}

export function getHolidays(year: number, countryCode?: string) {
  const hd = new Holidays(countryCode || "HU");
  const holidays = hd.getHolidays(year);
  return holidays.reduce((acc: any[], item: any) => {
    if (item.type === "public") {
      //only public holidays include
      acc.push(convertDate(item.date, "date"));
    }
    return acc;
  }, []);
}

export function sortArray<T>(
  data: T[],
  sortValue: keyof T,
  sortDirection: "asc" | "desc" | undefined
) {
  if (data && sortValue && sortDirection) {
    return sortDirection === "asc"
      ? [...data].sort((a, b) => (a[sortValue] < b[sortValue] ? -1 : 1))
      : [...data].sort((a, b) => (a[sortValue] > b[sortValue] ? -1 : 1));
  } else if (data && sortValue && !sortDirection) {
    return [...data].sort((a, b) => (a[sortValue] > b[sortValue] ? -1 : 1));
  } else {
    return data;
  }
}

export function findOption(id: number | undefined, options: optionType[]) {
  if (id === undefined || null) {
    return "-";
  }
  const value = options.find((item) => item.value === id);
  return value && value.label;
}

export function findHearAboutUsOption(
  id: number | undefined,
  hearAboutUsOptions: optionType[]
) {
  if (id === undefined || null) {
    return "-";
  }
  const value = hearAboutUsOptions.find((item) => item.value === id);
  return value && value.label;
}

export function findEventTypeOption(
  id: number | undefined,
  eventTypeOptions: optionType[]
) {
  if (id === undefined || null) {
    return "-";
  }
  const value = eventTypeOptions.find((item) => item.value === id);
  return value && value.label;
}

export function findTranslatedOption(
  id: number | undefined,
  options: optionType[],
  t: Translationtype,
  keyPrefix = "Options"
) {
  if (id === undefined || null) {
    return "-";
  }
  const value = options.find((item) => item.value === id);
  if (value && value.label) {
    return t(`${keyPrefix}.${value.label}` as any);
  }
  return "-";
}

export function findPaymentStatus(
  id: number | undefined,
  paymentOptions: optionType[]
) {
  if (id === undefined || null) {
    return "-";
  }
  const value = paymentOptions.find((item) => item.value === id);
  return value?.label;
}

export function couponUsedCalculator(maxValue: number, availableValue: number) {
  const calculated = maxValue - availableValue;
  return calculated > 0 ? calculated : 0;
}

export function findCounty(countryCode: any, county: string | undefined) {
  if (!county) {
    return "";
  }
  const search = countyOptions[countryCode as keyof countyOptionsType].find(
    (item: optionType) => item.value === county
  );
  return search ? ` ${search.label}, ` : "";
}

export function getFormattedNumber(number: number, currency: string) {
  const isWholeNumber = number % 1 === 0;
  return new Intl.NumberFormat("hu-HU", {
    style: "currency",
    minimumFractionDigits: currency === "HUF" || isWholeNumber ? 0 : 2,
    maximumFractionDigits: currency === "HUF" ? 0 : 2,
    currency,
  }).format(number);
}

export function formatPrice(price: number, currency?: string) {
  const cur = currency || "HUF";
  const fractionDigits = cur === "HUF" ? 0 : 2;
  return `${price.toLocaleString("hu-HU", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })} ${cur}`;
}

export function createElement(key: string, element: any, props?: any) {
  return cloneElement(element, { key, props });
}

export function downloadFile(url: string, fileName: string) {
  axios.get(url, { responseType: "blob" }).then((response) => {
    const _url = URL.createObjectURL(response.data);
    const a = document.createElement("a");
    a.href = _url;
    a.download = `${fileName}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(_url);
  });
}

export function handleIntlMessageFallback({ key }: any) {
  console.warn(`[MISSING TRANLSATION] ${key}`);
  return key;
}

export function isOdd(value: number) {
  return Math.abs(value % 2) == 1;
}

export function findRangeIndex(
  rangeArray: (number | null)[],
  number: number
): { range: null | number; index: number } {
  let idx = 0;
  if (rangeArray.length === 1) {
    return { range: null, index: idx };
  } else if (rangeArray[rangeArray.length - 2]! < number) {
    return { range: null, index: rangeArray.length - 1 };
  }
  let numItem = 0;
  for (let i = 0; i < rangeArray.length - 1; i++) {
    const numberItem = rangeArray[i];
    if (!numberItem) return { range: null, index: idx };
    if (number < numberItem) {
      numItem = numberItem;
      idx = i;
      break;
    }
  }
  return { range: numItem, index: idx };
}

export function parseSearchData(
  countryPayload: priceSearchResultType
): countryType | null {
  let country: countryType | null;
  if (!countryPayload || !countryPayload.context) {
    country = null;
  } else if (countryPayload.context.length > 1) {
    const cityName =
      countryPayload.place_type.length &&
      countryPayload.place_type[0] === "place"
        ? countryPayload.text
        : countryPayload.context[countryPayload.context.length - 2].text;
    country = {
      cityName,
      countryCode:
        countryPayload.context[
          countryPayload.context.length - 1
        ].short_code.toUpperCase(),
      stateCode:
        countryPayload.context[countryPayload.context.length - 2].short_code,
    };
  } else {
    country = {
      cityName: countryPayload.text,
      countryCode:
        countryPayload.context[
          countryPayload.context.length - 1
        ].short_code.toUpperCase(),
      stateCode: countryPayload.properties?.short_code || "",
    };
  }

  return country;
}

export async function isValidImageSrc(
  url: string | null | undefined
): Promise<boolean> {
  // Ellenőrizzük, hogy az URL érvényes-e
  if (!url || url === "null" || url === "undefined") {
    return false;
  }

  try {
    const response = await axios.head(url);
    const contentType = response.headers["content-type"];
    return contentType.startsWith("image/") ?? false;
  } catch (error) {
    console.error("Error fetching the URL:", error);
    return false;
  }
}

export function getFormattedDate(
  date: string,
  format = "YYYY-MM-DD HH:mm:ss",
  isUtc = false
) {
  if (isUtc) {
    return dayjs.utc(date).local().format(format);
  }

  return dayjs(date).format(format);
}

export function getFormattedFileSize(bytes: number) {
  if (bytes < 1024) {
    return `${bytes} Byte`;
  }

  if (bytes < Math.pow(1024, 2)) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${(bytes / Math.pow(1024, 2)).toFixed(1)} MB`;
}

export function subtractDate(
  date: string | null,
  unit: ManipulateType,
  value: number
): string | null {
  if (!date) {
    return null;
  } else {
    const _date = new Date(date);
    return dayjs(_date).subtract(value, unit).format();
  }
}

export function addDate(
  date: string | null,
  unit: ManipulateType,
  value: number
): string | null {
  if (!date) {
    return null;
  } else {
    const _date = new Date(date);
    return dayjs(_date).add(value, unit).format();
  }
}

export function getDescendants(
  data: parentIdOptionType[],
  value: string
): parentIdOptionType[] {
  const result: parentIdOptionType[] = [];

  function findChildren(parentId: string): void {
    data.forEach((item) => {
      if (item.parentId === parentId) {
        result.push(item);
        findChildren(item.value); // recursively find children
      }
    });
  }

  const root = data.find((item) => item.value === value);
  if (root) {
    result.push(root);
    findChildren(root.value);
  }

  return result;
}

export const getDateInterval = (
  startDate: DateType,
  endDate: DateType
): DateRangeType[] => {
  if (!startDate || !endDate) {
    return [];
  }
  // Check if the start date is the same or the next day as the end date, return empty array
  if (
    dayjs(startDate).add(1, "day").isSame(endDate) ||
    dayjs(startDate).isSame(endDate)
  ) {
    return [];
  }

  const intervalStart = dayjs(startDate).add(1, "day");

  const intervalEnd = dayjs(endDate).subtract(1, "day");

  return [
    {
      startDate: intervalStart.toDate(),
      endDate: intervalEnd.toDate(),
    },
  ];
};

export function isEventStartCloseToNow(eventStartDate: string) {
  return dayjs(eventStartDate).diff(dayjs(), "day") < 15;
}

export function calculateEventPayDeadline(eventStartDate: string) {
  return convertDate(
    dayjs(eventStartDate).add(-14, "day").toISOString(),
    "date"
  );
}

export function getEventImageSrc(eventType: number) {
  switch (eventType) {
    case 0:
      return "/assets/img/eventTypes/wedding.png";
    case 1:
      return "/assets/img/eventTypes/baby_shower.png";
    case 2:
      return "/assets/img/eventTypes/birthday.png";
    case 3:
      return "/assets/img/eventTypes/bachelor.png";
    case 4:
      return "/assets/img/eventTypes/bachelorette.png";
    case 5:
      return "/assets/img/eventTypes/wedding_present.png";
    case 7:
      return "/assets/img/eventTypes/company.png";
    case 6:
      return "/assets/img/eventTypes/other.png";
    default:
      return "/assets/img/eventTypes/other.png";
  }
}

export function getServicePackageName(
  servicePackage: string | null,
  t: Translationtype
) {
  if (servicePackage === "" || servicePackage === null) {
    return t("Pricing.servicePackage.defaultPackage");
  }
  return t("Pricing.servicePackage.magicAIPackage");
}

export function mapServicePackage(servicePackage: boolean) {
  return servicePackage ? "1" : "0";
}

export function formatCostInUSD(cost: number | null | undefined): string {
  return cost ? `$${cost.toFixed(2)}` : "-";
}

/**
 * Calculates remaining AI budget percentage.
 * @returns number (0-100), "unlimited", or null if AI is not active
 */
export function calculateAiBudgetPercent(
  magicServices: string | null | undefined,
  aiBudgetUsd: number | null | undefined,
  cost: number | null | undefined,
  aiBudgetPriceId?: string | null | undefined
): number | "unlimited" | null {
  // If AI is not activated → null (don't show)
  if (!magicServices) return null;

  // If there is an aiBudgetPriceId → budget package was purchased, always calculate
  if (aiBudgetPriceId) {
    if (!aiBudgetUsd || aiBudgetUsd === 0) return 0;
    const spent = cost || 0;
    const remaining = Math.max(0, (1 - spent / aiBudgetUsd) * 100);
    return Math.round(remaining);
  }

  // No aiBudgetPriceId but magicServices is set → manually configured by admin → unlimited
  return "unlimited";
}

export type PremiumServicesMode = "budget" | "quickPayment" | null;

/**
 * Decides how gallery guests can order premium AI images.
 * - "budget": generation is paid from the reservation's remaining AI budget
 * - "quickPayment": the guest pays; only offered when QuickPayment is enabled
 * - null: premium services are not offered
 */
export function getPremiumServicesMode(
  quickPayment: boolean | null | undefined,
  magicServices: string | null | undefined,
  aiBudgetUsd: number | null | undefined,
  cost: number | null | undefined
): PremiumServicesMode {
  if (!quickPayment && !magicServices) return null;

  const hasRemainingBudget =
    aiBudgetUsd != null && aiBudgetUsd > 0 && cost != null && cost < aiBudgetUsd;
  if (hasRemainingBudget) return "budget";

  return quickPayment ? "quickPayment" : null;
}

/**
 * Formats a price amount string for display.
 * For HUF / Ft amounts the decimal portion is stripped (e.g. "202000,00 HUF" -> "202000 HUF").
 * Other currencies (e.g. EUR) are returned unchanged.
 * Returns an empty string for null/undefined/empty input.
 */
export function formatDisplayAmount(
  amount: string | null | undefined
): string {
  if (!amount) return "";
  if (/(HUF|Ft)\s*$/i.test(amount)) {
    return amount.replace(/[.,]\d+(\s*(HUF|Ft))\s*$/i, "$1");
  }
  return amount;
}
